import { ref, computed, reactive, watch } from "vue";
import productService, { type Product } from "@/services/product";
import apiClient from "@/services/api";
import brandService from "@/services/brand";
import unitService from "@/services/unit";
import { sizeService } from "@/services/size";
import warehouseService from "@/services/warehouse";
import { useCurrentUser } from "@/composables/useCurrentUser";
import stateStore from "@/utils/store";
import { buildStorageAssetUrl } from "@/utils/settings";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

export function useProduct() {
  const { t, locale } = useI18n();
  const { fetchCurrentUser } = useCurrentUser();
  const products = ref<Product[]>([]);
  const loading = ref(false);
  const importInput = ref<HTMLInputElement | null>(null);
  const total = ref(0);

  // Filters
  const searchQuery = ref("");
  const selectedCategoryId = ref<number>(0);
  const selectedBrandId = ref<number>(0);
  const selectedUnitId = ref<number>(0);
  const selectedSizeId = ref<number>(0);
  const selectedWarehouseId = ref<number>(0);

  // Filter Options
  const categories = ref<{ id?: number; name?: string }[]>([]);
  const brands = ref<{ id?: number; name?: string }[]>([]);
  const units = ref<{ id?: number; name?: string }[]>([]);
  const sizes = ref<{ id?: number; name?: string }[]>([]);
  const warehouses = ref<{ id?: number; name?: string }[]>([]);

  // Selection
  const selectedIds = ref<number[]>([]);
  const profileWarehouseId = ref<number>(0);
  const warehouseLockedToProfile = computed(() => profileWarehouseId.value > 0);

  const fetchProducts = async (page = paginationConfig.current, pageSize = paginationConfig.pageSize) => {
    loading.value = true;
    try {
      const response = await productService.getProductsPage({
        page,
        per_page: pageSize,
        search: searchQuery.value || undefined,
        warehouse_id: selectedWarehouseId.value || undefined,
        category_id: selectedCategoryId.value || undefined,
        brand_id: selectedBrandId.value || undefined,
        unit_id: selectedUnitId.value || undefined,
        size_id: selectedSizeId.value || undefined,
      });
      products.value = response.data ?? [];
      total.value = Number(response.total ?? 0);
      paginationConfig.current = Number(response.current_page ?? page);
      paginationConfig.pageSize = Number(response.per_page ?? pageSize);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      loading.value = false;
    }
  };

  const fetchFiltersData = async () => {
    try {
      const [catsRes, brandsRes, unitsRes, sizesRes, whRes] = await Promise.all([
        apiClient.get("/categories"),
        brandService.getBrands(),
        unitService.getUnits(),
        sizeService.getSizes(),
        warehouseService.getWarehouses(),
      ]);
      categories.value = (catsRes.data?.data ?? catsRes.data) || [];
      brands.value = brandsRes || [];
      units.value = unitsRes || [];
      sizes.value = sizesRes || [];
      warehouses.value = whRes || [];
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  const initializeWarehouseContext = async () => {
    try {
      const currentUser = await fetchCurrentUser(true);
      const warehouseId = Number(currentUser?.warehouse_id || 0);
      profileWarehouseId.value = warehouseId;

      if (warehouseId > 0) {
        selectedWarehouseId.value = warehouseId;
      }
    } catch (error) {
      console.error("Error loading current user warehouse:", error);
    }
  };

  const filteredProducts = computed(() => products.value);

  const paginationConfig = reactive({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100", "1000"],
    showTotal: (total: number, range: [number, number]) =>
      t("product_page.showing_results", {
        from: range[0],
        to: range[1],
        total,
      }),
    locale: { items_per_page: t("product_page.items_per_page") },
  });

  watch(locale, () => {
    paginationConfig.locale = { items_per_page: t("product_page.items_per_page") };
  });

  watch(total, (newVal) => {
    paginationConfig.total = newVal;
    const totalCount = newVal > 0 ? newVal : 1000;
    const options = ["10", "20", "30", "50", "100"];
    if (totalCount > 100) options.push(String(totalCount));
    paginationConfig.pageSizeOptions = options;
  }, { immediate: true });

  watch(() => stateStore.refreshTrigger, () => {
    fetchProducts(1, paginationConfig.pageSize);
  });

  watch(selectedWarehouseId, () => {
    fetchProducts(1, paginationConfig.pageSize);
  });

  watch(
    [searchQuery, selectedCategoryId, selectedBrandId, selectedUnitId, selectedSizeId],
    () => {
      fetchProducts(1, paginationConfig.pageSize);
    }
  );

  const deleteProduct = async (product: Product) => {
    if (!product?.id) return;

    const result = await Swal.fire({
      title: t("product_page.delete_product_title"),
      text: t("product_page.delete_product_text", { name: product.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("product_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("product_page.deleting"),
        text: t("product_page.please_wait"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await productService.deleteProduct(product.id);
      await fetchProducts(paginationConfig.current, paginationConfig.pageSize);

      Swal.fire({
        icon: "success",
        title: t("product_page.deleted_title"),
        text: t("product_page.deleted_product_text", { name: product.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Delete product error:", error);
      Swal.fire({
        icon: "error",
        title: t("product_page.delete_failed"),
        text: t("product_page.delete_failed_message"),
      });
    }
  };

  const toggleEmenuVisibility = async (product: Product, value: boolean) => {
    if (!product?.id) return;

    const previousValue = Boolean(product.show_on_emenu);
    product.show_on_emenu = value;

    try {
      const updatedProduct = await productService.updateEmenuVisibility(product.id, value);
      const index = products.value.findIndex((item) => item.id === product.id);
      if (index !== -1) {
        products.value[index] = {
          ...products.value[index],
          ...updatedProduct,
        };
      }
    } catch (error) {
      product.show_on_emenu = previousValue;
      console.error("Update product E-Menu visibility error:", error);
      Swal.fire({
        icon: "error",
        title: t("product_page.error_title"),
        text: t("product_page.emenu_update_failed"),
      });
    }
  };

  const getImageUrl = (p: Product): string => {
    const mainAtt = p.attachments?.find((a) => a.is_main === 1) || p.attachments?.[0];
    const filePath = mainAtt?.file_path || (mainAtt as any)?.path;
    return buildStorageAssetUrl(filePath);
  };

  const toStockNumber = (value?: number) => Number(value ?? 0);

  const getWarehouseStocks = (p: Product) => {
    const stocks = p.stocks || [];
    const activeWarehouseId = selectedWarehouseId.value || profileWarehouseId.value;

    if (activeWarehouseId > 0) {
      return stocks.filter((stock) => Number(stock.warehouse_id) === Number(activeWarehouseId));
    }

    return stocks;
  };

  const getDisplayQty = (p: Product) => {
    const relevantStocks = getWarehouseStocks(p);

    if (relevantStocks.length > 0) {
      return relevantStocks.reduce((total, stock) => total + toStockNumber(stock.qty ?? stock.quantity), 0);
    }

    return 0;
  };

  const getSizeLabel = (p: Product) => {
    if (p.product_sizes?.length) {
      const names = p.product_sizes
        .map((ps) => ps.size?.name)
        .filter((name): name is string => Boolean(name));

      if (names.length) {
        return names.join(", ");
      }
    }

    return p.size?.name || t("product_page.not_available");
  };

  const exportExcel = async () => {
    try {
      const ids = selectedIds.value.slice();
      const blob = await productService.exportProducts("excel", ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("product_page.error_title"),
        text: t("product_page.export_excel_failed"),
      });
      console.error("Export Excel error:", error);
    }
  };

  const exportPdf = async () => {
    try {
      const blob = await productService.exportProducts("pdf");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("product_page.error_title"),
        text: t("product_page.export_pdf_failed"),
      });
      console.error("Export PDF error:", error);
    }
  };

  const triggerImport = () => {
    importInput.value?.click();
  };

  const handleImportChange = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    Swal.fire({
      title: t("product_page.importing"),
      text: t("product_page.import_processing_file"),
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const res = await productService.importProducts(file);
      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: t("product_page.import_success_title"),
          text: t("product_page.import_success_text"),
          timer: 1500,
          showConfirmButton: false,
        });
        await fetchProducts(1, paginationConfig.pageSize);
      } else {
        Swal.fire({
          icon: "warning",
          title: t("product_page.import_finished"),
          text: res?.message || t("product_page.import_finished_with_messages"),
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("product_page.import_failed_title"),
        text: t("product_page.import_failed_text"),
      });
      console.error("Import error:", error);
    } finally {
      input.value = "";
    }
  };

  const handleTableChange = (pager: { current?: number; pageSize?: number }) => {
    fetchProducts(pager.current ?? 1, pager.pageSize ?? paginationConfig.pageSize);
  };

  return {
    products,
    filteredProducts,
    loading,
    importInput,
    searchQuery,
    selectedCategoryId,
    selectedBrandId,
    selectedUnitId,
    selectedSizeId,
    selectedWarehouseId,
    categories,
    brands,
    units,
    sizes,
    warehouses,
    selectedIds,
    paginationConfig,
    total,
    stateStore,
    profileWarehouseId,
    warehouseLockedToProfile,
    fetchProducts,
    fetchFiltersData,
    initializeWarehouseContext,
    deleteProduct,
    toggleEmenuVisibility,
    getImageUrl,
    getDisplayQty,
    getSizeLabel,
    getWarehouseStocks,
    exportExcel,
    exportPdf,
    triggerImport,
    handleImportChange,
    handleTableChange,
  };
}
